import { TRefElementInstance } from 'widgets/toolbox/RefElement';
import { clickOutside } from 'widgets/toolbox/util';
const KEY_DOWN = 40;
const KEY_UP = 38;
const KEY_ESCAPE = 27;
const KEY_RETURN = 13;
const KEY_TAB = 9;
const ACTIVE_DESCENDANT = 'aria-activedescendant';

/**
 * @typedef {InstanceType<typeof import('widgets/toolbox/RefElement').RefElement>} refElement
 */

/**
 * @description Base Combobox implementation
 * @param BasicInput Base widget for extending
 * @returns Combobox class
 */
function ComboboxClassCreator(BasicInput: import('widgets/forms/BasicInput').TBasicInput) {
    /*
     * This content is based on w3.org design pattern examples and licensed according to the
     * W3C Software License at
     * https://www.w3.org/Consortium/Legal/2015/copyright-software-and-document
     * Please see specification:
     * https://www.w3.org/TR/wai-aria-practices/#combobox
     */
    /**
     * @category widgets
     * @subcategory forms
     * @class Combobox
     * @augments BasicInput
     * @classdesc Combobox implementation in accordance with accessibility statements.
     * Class could be extended to implement any provider-specific remote/local data fetching for Combobox inner elements.
     * It is needed to implement `getSuggestions` method in a way, that is specific for subclasses
     * Supports keyboard navigation among Combobox items.
     * As far as Combobox could be used only in a subclasses, example, given below, is related to known subclass `searchBox`
     * @property {string} data-widget - Widget name `searchBox`
     * @property {string} data-url - URL to obtain search suggestions from server based on customer's input
     * @property {boolean} data-close-on-tab - If true - `tab` keypress will close listbox
     * @property {string} data-widget-event-closesearch - An event, fired when `close` element was pressed
     * @property {string} data-event-keydown - event handler for `keydown` event
     * @property {boolean} data-close-from-outside - config, which shows, if combobox should be closed when click outside
     * @property {number} data-min-chars - minimum characters to trigger show combobox
     * @property {number} data-max-chars - maximum characters to enter
     * @property {number} data-updating-delay - update listbox delay
     * @property {string} data-classes-focused-item - class for currently focused item
     * @property {string} data-classes-active-list-box - active listbox class
     * @example <caption>Example of Combobox widget usage</caption>
     * <div
     *     id="search-box-dialog"
     *     data-widget="searchBox"
     *     data-disable-rendering="true"
     *     data-ref-first-focus-element="submit"
     *     data-ref-last-focus-element="closeSearch"
     *     data-focus-loop="true"
     *     data-event-keydown="handleKeydown"
     *     data-url="${URLUtils.url('SearchServices-GetSuggestions')}"
     *     data-close-from-outside="false"
     *     data-close-on-tab="false"
     *     data-ref-traps-container="formWrap"
     * >
     *     <form>
     *         ... search field content
     *     </form>
     *     ...
     *     <div
     *        aria-labelledby="header-search-input"
     *        id="search-suggestions-list"
     *        class="b-suggestions"
     *        data-ref="listbox"
     *        data-event-mouseenter="markHover"
     *        data-event-mouseleave="unMarkHover"
     *        data-tau="search_suggestions_list"
     *     >
     *         <div role="none" class="b-suggestions-inner" data-ref="listboxInner"></div>
     *     </div>
     *     <script data-ref="template" type="template/mustache">
     *         <div data-ref="listboxInner">
     *             ... search suggestions
     *         </div>
     *     </script>
     * </div>
     */
    class Combobox extends BasicInput {
        currentSelected?: TRefElementInstance | null;

        bodyClickListener?: ReturnType<typeof clickOutside>;

        timeout?: ReturnType<typeof setTimeout>;

        lastSearchedTerm = '';

        selectedIndex = -1;

        resultsCount = 0;

        isListboxOpen = false;

        isOverlayVisible = false;

        hasHoverWithin = false;

        prefs() {
            return {
                minChars: 3,
                maxChars: 50,
                updatingDelay: 300,
                closeFromOutside: true,
                closeOnTab: true,
                classesFocusedItem: 'm-focused',
                classesActiveListBox: 'm-active',
                ...super.prefs()
            };
        }

        /**
         * @description Handles customer input
         */
        handleInput() {
            const inputLength = this.ref('field').prop('value').length;

            if (inputLength >= this.prefs().minChars && inputLength <= this.prefs().maxChars) {
                this.updateListbox();
            } else {
                if (this.timeout) {
                    clearTimeout(this.timeout);
                }

                this.closeListbox();
            }
        }

        /**
         * @description Marks element as hover, once mouse over
         * @listens dom#mouseenter
         */
        markHover() {
            this.hasHoverWithin = true;
        }

        /**
         * @description Un-mark element as hover, once mouse out
         * @listens dom#mouseleave
         */
        unMarkHover() {
            this.hasHoverWithin = false;
        }

        /**
         * @description Handles `focus` on combobox input
         * @listens dom#focus
         */
        handleFocus() {
            this.updateListbox();
        }

        /**
         * @description Handles `blur` on combobox input
         * @listens dom#blur
         */
        handleBlur() {
            if (this.hasHoverWithin || this.selectedIndex < 0) {
                return;
            }

            this.closeListbox();
        }

        /**
         * @description Handles `keydown` on combobox input
         * @param _ - source of keydown event
         * @param event - keydown event object
         * @listens dom#keydown
         */
        handleKeydown(_: HTMLElement, event: KeyboardEvent) {
            let preventEventActions = false;

            switch (event.keyCode) {
                case KEY_ESCAPE:
                    this.closeListbox();
                    preventEventActions = true;
                    break;
                case KEY_UP:
                    this.setSelectToPreviousItem();
                    this.selectItem(this.getItemByIndex());
                    preventEventActions = true;
                    break;
                case KEY_DOWN:
                    this.setSelectToNextItem();
                    this.selectItem(this.getItemByIndex());
                    preventEventActions = true;
                    break;
                case KEY_RETURN:
                    this.activateItem(this.getItemByIndex());

                    return;
                case KEY_TAB:
                    if (this.prefs().closeOnTab) {
                        this.closeListbox();
                    }

                    return;
                default:
                    return;
            }

            if (preventEventActions) {
                event.stopPropagation();
                event.preventDefault();
            }
        }

        /**
         * @description Sets previous item as selected, when navigating from keyboard
         */
        setSelectToNextItem() {
            if (this.selectedIndex === -1 || this.selectedIndex >= this.resultsCount) {
                this.selectedIndex = 0;
            } else {
                this.selectedIndex += 1;
            }
        }

        /**
         * @description Sets next item as selected, when navigating from keyboard
         */
        setSelectToPreviousItem() {
            if (this.selectedIndex <= 0) {
                this.selectedIndex = this.resultsCount - 1;
            } else {
                this.selectedIndex -= 1;
            }
        }

        /**
         * @description Selects combobox dropdown item
         * @param selectedItem - item, selected by user
         */
        selectItem(selectedItem) {
            if (this.currentSelected) {
                this.deselectItem(this.currentSelected);
            }

            if (selectedItem) {
                this.ref('field').attr(ACTIVE_DESCENDANT, `result-item-${this.selectedIndex}`);
                selectedItem
                    .addClass(this.prefs().classesFocusedItem)
                    .attr('aria-selected', 'true');
                this.afterItemSelected(selectedItem);
            } else {
                this.ref('field').attr(ACTIVE_DESCENDANT, false);
            }

            this.currentSelected = selectedItem;
        }

        /**
         * @description Executes logic (sets input value) after item was selected
         * @param item - item, selected by user
         */
        afterItemSelected(item: TRefElementInstance) {
            this.ref('field').prop('value', item.data<string>('suggestionValue'));
        }

        /**
         * @description Deselects item in combobox dropdown when navigating from keyboard.
         * @param item - item, selected by user
         */
        deselectItem(item: TRefElementInstance) {
            item
                .removeClass(this.prefs().classesFocusedItem)
                .attr('aria-selected', 'false');
        }

        /**
         * @description "Activate" item by press Enter on selected item or mouse click on item
         * @param activeItem - item, selected by user
         */
        activateItem(activeItem: TRefElementInstance | null) {
            if (activeItem) {
                this.ref('field')
                    .prop('value', activeItem.data<string>('suggestionValue'))
                    .get().focus();
                this.closeListbox();
            }
        }

        /**
         * @description Update Combobox Listbox (dropdown). Initiates items retrieving either from server or from any other source, like an array.
         */
        updateListbox() {
            const inputValue = this.ref('field').prop('value');

            if (this.lastSearchedTerm === inputValue || inputValue.length < this.prefs().minChars) {
                // could be triggered from focus so we need additionally check minChars
                return;
            }

            if (this.timeout) {
                clearTimeout(this.timeout);
            }

            this.timeout = setTimeout(this.getSuggestions.bind(this, inputValue), this.prefs().updatingDelay);
        }

        /**
         * @description Get items list for Combobox. Could be fetched from any proper source.
         * Usually is implemented in children.
         * @param query - a query, which needs to be processed in subclasses
         */
        getSuggestions(query: string) {
            // This is example of getSuggestion method to implement
            // Combo boxes like search or address should implement his own methods
            // of how to get suggestion and render it. Template should follow this:
            // <li role="option" id="result-item-${0}">${results[0]}</li>
            // ...
            // <li role="status" aria-live="polite">${results.length} suggestion found</li>
            // You should always include ids of suggestion and status about how much
            // suggestion total.
            const resultsTotal = 1;

            this.afterSuggestionsUpdate(query, resultsTotal);
        }

        /**
         * @description Executes after a list of items (suggestions) of a Combobox has being updated.
         * @param query - a query, used for fetching Combobox inner items
         * @param resultsCount - obtained inner items count
         */
        afterSuggestionsUpdate(query: string, resultsCount: number) {
            this.resultsCount = resultsCount;

            this.selectedIndex = -1;

            this.lastSearchedTerm = query;

            if (resultsCount > 0) {
                this.openListbox();
            }
        }

        /**
         * @description Open a Listbox (Combobox dropdown)
         */
        openListbox() {
            this.isListboxOpen = true;
            const listbox = this.ref('listbox');

            listbox.addClass(this.prefs().classesActiveListBox);
            listbox.attr('aria-hidden', 'false');
            const input = this.ref('field');

            input.attr(ACTIVE_DESCENDANT, false);
            input.attr('aria-expanded', 'true'); // Should be combobox node by specs
            this.toggleOverlay(true);

            if (this.prefs().closeFromOutside) {
                this.bodyClickListener = clickOutside(this.ref('self'), this.closeListbox.bind(this), false);

                if (this.bodyClickListener) {
                    this.onDestroy(this.bodyClickListener);
                }
            }
        }

        /**
         * @description Close a Listbox (Combobox dropdown)
         */
        closeListbox() {
            this.resultsCount = 0;

            this.selectedIndex = -1;

            this.lastSearchedTerm = '';

            this.isListboxOpen = false;

            const listbox = this.ref('listbox');

            listbox.removeClass(this.prefs().classesActiveListBox);
            listbox.attr('aria-hidden', 'true');

            const input = this.ref('field');

            input.attr(ACTIVE_DESCENDANT, '');
            input.attr('aria-expanded', 'false'); // Should be combobox not by specs
            this.toggleOverlay(false);

            if (this.bodyClickListener) {
                this.bodyClickListener();

                this.bodyClickListener = undefined;
            }

            this.afterCloseListbox();
        }

        /**
         * @description Executes after Listbox (Combobox dropdown) was closed
         */
        afterCloseListbox() {
            this.ref('listbox').empty();
        }

        /**
         * @description Toggles Combobox page overlay
         * @param isShown - does overlay should be shown
         */
        toggleOverlay(isShown: boolean) {
            this.isOverlayVisible = isShown;
        }

        /**
         * @description Get Combobox item (suggestion) by index, stored in `this.selectedIndex` class property.
         * @returns Founded item or null
         */
        getItemByIndex() {
            if (this.selectedIndex < 0) {
                return null;
            }

            const listItem = this.ref(`result-item-${this.selectedIndex}`);

            if (listItem.length) {
                return listItem;
            }

            return null;
        }
    }

    return Combobox;
}

export type TCombobox = ReturnType<typeof ComboboxClassCreator>;

export type TComboboxInstance = InstanceType<TCombobox>;

export default ComboboxClassCreator;
